// The dashboard's contract. The plugin `shared-dashboard` provides the
// dashboard's pieces as slots: a section of cards (`dashboard.section`), a
// band of sections under a scope label (`dashboard.band`), the card frame
// (`dashboard.card`) and the parts a composer draws with
// (`dashboard.parts`). A card stays its own plugin (aggregator, body, help
// topic, tests, stories) and draws no frame: it registers its body on a
// section's slot with `registerCard`, and the section wraps it in the frame.
//
//   registerCard(app, 'home.section.summary', RatingBody, { order: 10, title: 'Rating', help: 'rating' });
//
// A contribution registered without card metadata (or with `frame: 'none'`)
// is rendered as it is, so a section takes old-style cards and composers too.
import type { ComponentType, ReactNode } from 'react';

/** A section of cards under an optional title: `params` are `SectionParams`. */
export const DASHBOARD_SECTION = 'dashboard.section';
/** A tinted band of sections under a scope label: `params` are `BandParams`. */
export const DASHBOARD_BAND = 'dashboard.band';
/** One card frame around its children: `params` are `CardMeta` (an escape hatch; cards use `registerCard`). */
export const DASHBOARD_CARD = 'dashboard.card';
/** Provides the real `Card` and `Section` to `useDashboardParts` for whatever it wraps (`./react`). */
export const DASHBOARD_PARTS = 'dashboard.parts';

/** At most this many cards in a row on any dashboard (`--dash-max-columns`). */
export const MAX_COLUMNS = 3;
/** A card column's least width before the row wraps, in rem. */
export const CARD_MIN_REM = 16;
/** A card column's greatest width, in rem (`--home-section-card-max`). */
export const CARD_MAX_REM = 24;
/** The gap between a section's columns, in rem (`--home-section-gap`). */
export const CARD_GAP_REM = 1;

/** What the frame draws around a card's body. */
export interface CardMeta {
  /** The card's title, in the head. */
  title: string;
  /** A help topic's id (`@chess/api-help`): the frame puts its "?" at the head's right. */
  help?: string;
  /** Spans the whole row. */
  wide?: boolean;
  /** `'none'`: the body is rendered as it is, without the frame (it draws its own). */
  frame?: 'card' | 'none';
}

/** A card's registration defaults: its place in the section and its frame. */
export interface CardDefaults {
  order: number;
  card: CardMeta;
}

/** The screen a section stands for: its header links there. */
export interface SectionScreen {
  /** The screen's address. */
  to: string;
  /** Phosphor icon path data of the screen's rail entry, viewBox 0 0 256 256. */
  icon?: string;
  /** The link at the header's right; "See all" unless given. */
  label?: string;
}

/** What a screen passes to `dashboard.section`. */
export interface SectionParams {
  /** Omitted where the screen's own header already names it: the cards then stand without a title. */
  title?: string;
  /** The slot its cards register on, ordered by `order`. */
  slot?: string;
  /** The screen it stands for: its header links there. */
  screen?: SectionScreen;
  /** A help topic's id, its "?" after the title. */
  help?: string;
  /** Shown while no card has joined the section, e.g. "Middlegame cards are coming." */
  empty?: string;
  /** At most this many cards in a row; only ever fewer than `MAX_COLUMNS`. */
  maxColumns?: number;
  /** A slot rendered under the cards, within their width (e.g. the openings screen's tables). */
  after?: string;
  /** A quiet label over the cards, within their width ("Against your history"). */
  label?: string;
  /** The header's content, in place of the title and the screen link (a composer's own heading). */
  head?: ReactNode;
}

/** What a screen passes to `dashboard.band`. */
export interface BandParams {
  /** The slot the band's sections register on, ordered by `order`. */
  slot: string;
  /** The band's scope, "Last 30 days" by default. */
  label?: string;
  /**
   * Untitled sections that read as one dashboard: spaced as the cards' rows
   * are (`--dash-gap`) rather than by the dashboard's section gap.
   */
  continuous?: boolean;
}

/** The part of a plugin's `app` that `registerCard` needs (the platform's `PiletApi` is one). */
export interface CardRegistrar {
  registerExtension(name: string, Component: ComponentType<any>, defaults?: any): () => void;
}

/**
 * Registers a card's body on a section's slot, with the frame's metadata as
 * its registration defaults (`CardDefaults`); the section draws the frame.
 * Returns the removal.
 */
export function registerCard(app: CardRegistrar, slot: string, Body: ComponentType<any>, options: { order: number } & CardMeta): () => void {
  const { order, ...card } = options;
  const defaults: CardDefaults = { order, card };
  return app.registerExtension(slot, Body, defaults);
}

/** The card metadata in a registration's defaults, when it asks for the frame; undefined for one rendered as it is. */
export function frameOf(defaults: unknown): CardMeta | undefined {
  const card = (defaults as Partial<CardDefaults> | undefined)?.card;
  if (!card || typeof card !== 'object' || card.frame === 'none') return undefined;
  return card;
}
